import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStore } from '../context/StoreContext';
import { subscriptionsAPI, extractErrorMessage } from '../api';
import { useToast } from '../components/Toast';

const TIER_LABELS = {
  trial: 'Trial',
  free: 'Free',
  basic: 'Basic',
  starter: 'Starter',
  growth: 'Growth',
};

const TIER_COLORS = {
  trial: '#6C63FF',
  free: '#94A3B8',
  basic: '#3B82F6',
  starter: '#6C63FF',
  growth: '#10B981',
};

const STATUS_LABELS = {
  trialing: 'Trial',
  active: 'Active',
  past_due: 'Past Due',
  canceled: 'Canceled',
  expired: 'Expired',
};

const STATUS_CLASSES = {
  trialing: 'badge-info',
  active: 'badge-success',
  past_due: 'badge-warning',
  canceled: 'badge-danger',
  expired: 'badge-danger',
};

export default function Billing() {
  const { selectedStore } = useStore();
  const navigate = useNavigate();
  const showToast = useToast();
  const [loading, setLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);
  const [subData, setSubData] = useState(null);
  const [payments, setPayments] = useState([]);
  const [error, setError] = useState('');

  const loadData = useCallback(async () => {
    if (!selectedStore) return;
    setLoading(true);
    try {
      const [subRes, paymentsRes] = await Promise.allSettled([
        subscriptionsAPI.getStoreSubscription(selectedStore.id),
        subscriptionsAPI.getPaymentHistory(selectedStore.id),
      ]);
      if (subRes.status === 'fulfilled') setSubData(subRes.value.data);
      if (paymentsRes.status === 'fulfilled') setPayments(paymentsRes.value.data);
    } catch (e) {
      setError(extractErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [selectedStore]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleManageBilling = async () => {
    setPortalLoading(true);
    try {
      const res = await subscriptionsAPI.getBillingPortal(selectedStore.id);
      if (res.data.portal_url) {
        window.location.href = res.data.portal_url;
      }
    } catch (e) {
      showToast(extractErrorMessage(e, 'Failed to open billing portal'), 'error');
    } finally {
      setPortalLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription?')) return;
    try {
      await subscriptionsAPI.cancel(selectedStore.id);
      loadData();
    } catch (e) {
      setError(extractErrorMessage(e));
    }
  };

  if (loading) {
    return (
      <div className="loading"><div className="spinner"></div><span>Loading billing...</span></div>
    );
  }

  const sub = subData?.subscription;
  const usage = subData?.usage;
  const tierColor = TIER_COLORS[sub?.tier] || '#94A3B8';

  const usageItems = usage ? [
    { label: 'Products', used: usage.products_used, limit: usage.products_limit },
    { label: 'Staff', used: usage.staff_used, limit: usage.staff_limit },
    { label: 'Locations', used: usage.locations_used, limit: usage.locations_limit },
    { label: 'Monthly Sales', used: usage.monthly_sales_used, limit: usage.monthly_sales_limit },
  ] : [];

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1 className="page-title">Billing & Subscription</h1>
          <p className="page-subtitle">Manage your plan, usage, and payment history</p>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {/* Current Plan Card */}
      <div className="billing-plan-card" style={{ borderLeftColor: tierColor }}>
        <div className="billing-plan-header">
          <div>
            <span className="billing-plan-tier" style={{ color: tierColor }}>
              {TIER_LABELS[sub?.tier] || 'Free'} Plan
            </span>
            <span className={`badge ${STATUS_CLASSES[sub?.status] || 'badge-info'}`} style={{ marginLeft: 12 }}>
              {STATUS_LABELS[sub?.status] || sub?.status}
            </span>
          </div>
          <div className="billing-plan-price">
            <span className="billing-plan-amount">${sub?.monthly_price || 0}</span>
            <span className="billing-plan-period">/month USD</span>
          </div>
        </div>

        {sub?.status === 'trialing' && sub?.trial_ends_at && (
          <div className="billing-trial-notice">
            {sub.trial_days_remaining != null && sub.trial_days_remaining > 0 ? (
              <strong>{sub.trial_days_remaining} day{sub.trial_days_remaining !== 1 ? 's' : ''} remaining</strong>
            ) : null}
            {' '}— Trial ends {new Date(sub.trial_ends_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            . Full access to all features during trial.
          </div>
        )}

        {sub?.billing_cycle_end && sub?.status === 'active' && (
          <div className="billing-cycle-info">
            Next billing date: {new Date(sub.billing_cycle_end).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
          </div>
        )}

        {sub?.status === 'past_due' && (
          <div className="billing-past-due-notice">
            Payment failed. Please update your payment method to avoid service interruption.
          </div>
        )}

        <div className="billing-plan-actions">
          <button className="btn btn-primary" onClick={() => navigate('/pricing')}>
            {sub?.tier === 'free' || sub?.status === 'trialing' ? 'Upgrade Plan' : 'Change Plan'}
          </button>
          {sub?.stripe_customer_id && (
            <button className="btn btn-secondary" onClick={handleManageBilling} disabled={portalLoading}>
              {portalLoading ? 'Opening...' : 'Manage Billing'}
            </button>
          )}
          {sub?.status === 'active' && sub?.tier !== 'free' && (
            <button className="btn btn-ghost" onClick={handleCancel}>
              Cancel Subscription
            </button>
          )}
        </div>
      </div>

      {/* Usage Section */}
      <div className="billing-section">
        <h2 className="billing-section-title">Usage</h2>
        <div className="billing-usage-grid">
          {usageItems.map((item) => {
            const isUnlimited = item.limit === -1;
            const pct = isUnlimited ? 10 : item.limit > 0 ? Math.min(100, (item.used / item.limit) * 100) : 0;
            const isHigh = pct > 80;
            return (
              <div key={item.label} className="billing-usage-item">
                <div className="billing-usage-label">
                  <span>{item.label}</span>
                  <span className="billing-usage-count">
                    {item.used ?? 0} / {isUnlimited ? 'Unlimited' : (item.limit ?? 0).toLocaleString()}
                  </span>
                </div>
                <div className="billing-usage-bar-track">
                  <div
                    className="billing-usage-bar-fill"
                    style={{
                      width: `${pct}%`,
                      backgroundColor: isHigh ? '#EF4444' : tierColor,
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Payment History */}
      <div className="billing-section">
        <h2 className="billing-section-title">Payment History</h2>
        {payments.length === 0 ? (
          <div className="billing-empty">No payment history yet.</div>
        ) : (
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Description</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Invoice</th>
                </tr>
              </thead>
              <tbody>
                {payments.map((p) => (
                  <tr key={p.id}>
                    <td>{new Date(p.created_at).toLocaleDateString()}</td>
                    <td>{p.description || 'Subscription payment'}</td>
                    <td>${Number(p.amount).toFixed(2)} USD</td>
                    <td>
                      <span className={`badge ${p.status === 'succeeded' ? 'badge-success' : p.status === 'failed' ? 'badge-danger' : 'badge-info'}`}>
                        {p.status}
                      </span>
                    </td>
                    <td>
                      {p.invoice_url ? (
                        <a href={p.invoice_url} target="_blank" rel="noopener noreferrer" className="link-primary">
                          View
                        </a>
                      ) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

    </div>
  );
}
